<template>
  <div class="ts-page">
    <div class="ts-card">
      <!-- Error / Expired state -->
      <div v-if="pageError" class="ts-error-state">
        <div class="ts-icon ts-icon-error">&#10007;</div>
        <p>{{ pageError }}</p>
      </div>

      <!-- Loading state -->
      <div v-else-if="loading" class="ts-loading">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Loading available times...</p>
      </div>

      <!-- Confirmation state -->
      <div v-else-if="booked" class="ts-confirmed">
        <div class="ts-icon ts-icon-success">&#10003;</div>
        <h2>Appointment Confirmed</h2>
        <p class="ts-confirm-details">
          <strong>{{ bookedDetails.address }}</strong
          ><br />
          {{ formatDateLong(bookedDetails.date) }}<br />
          {{ bookedDetails.startTime }} &ndash; {{ bookedDetails.endTime }}
        </p>
        <p v-if="bookedDetails.fee && parseFloat(bookedDetails.fee) > 0">
          Fee: <strong>${{ parseFloat(bookedDetails.fee).toFixed(2) }}</strong>
        </p>
        <p class="ts-note">Please be home during this time. Thank you!</p>
      </div>

      <!-- Scheduling flow -->
      <div v-else>
        <h2 class="ts-title">Schedule Maintenance</h2>
        <p class="ts-address">{{ address }}</p>
        <p v-if="fee && parseFloat(fee) > 0" class="ts-fee">
          Scheduling fee: <strong>${{ parseFloat(fee).toFixed(2) }}</strong>
        </p>

        <!-- Step 1: Day picker -->
        <div v-if="step === 'day'" class="ts-step">
          <p class="ts-instruction">Select a day:</p>
          <div class="ts-day-grid">
            <div
              v-for="n in dayOffset"
              :key="'sp-' + n"
              class="ts-day-spacer"
            ></div>
            <div
              v-for="day in workDays"
              :key="day.date"
              class="ts-day-card"
              :class="{
                'ts-day-unavailable': !day.blocks.some((b) => b.available),
              }"
              @click="day.blocks.some((b) => b.available) && selectDay(day)"
            >
              <div class="ts-day-name">{{ day.dayOfWeek }}</div>
              <div class="ts-day-date">{{ day.month }}/{{ day.dayNumber }}</div>
            </div>
          </div>
          <div class="ts-nav-row">
            <button
              v-if="navStartDate"
              class="ts-btn-nav"
              @click="loadPrevious"
            >
              <i class="fas fa-arrow-left"></i> Previous
            </button>
            <span v-else></span>
            <button class="ts-btn-nav" @click="loadNext">
              Future <i class="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>

        <!-- Step 2: Time picker -->
        <div v-if="step === 'time'" class="ts-step">
          <p class="ts-instruction">
            {{ selectedDay.dayOfWeek }}, {{ selectedDay.month }}/{{
              selectedDay.dayNumber
            }}
            &mdash; Pick a time:
          </p>
          <div class="ts-time-list">
            <div
              v-for="block in selectedDay.blocks"
              :key="block.start"
              class="ts-time-card"
              :class="{ 'ts-time-unavail': !block.available }"
              @click="block.available && selectBlock(block)"
            >
              {{ block.label }}
              <span v-if="!block.available" class="ts-badge">Unavailable</span>
            </div>
          </div>
          <button class="ts-btn-nav" @click="step = 'day'">
            <i class="fas fa-arrow-left"></i> Back
          </button>
        </div>

        <!-- Step 3: Phone verification -->
        <div v-if="step === 'verify'" class="ts-step">
          <p class="ts-instruction">
            Confirm your identity<span v-if="tenantName"
              >, {{ tenantName }}</span
            >
          </p>
          <p class="ts-sub-instruction">
            {{ selectedDay.dayOfWeek }}, {{ selectedDay.month }}/{{
              selectedDay.dayNumber
            }}<br />
            {{ selectedBlock.label }}
          </p>
          <label class="ts-label">Phone number on file:</label>
          <input
            v-model="phone"
            type="tel"
            class="ts-input"
            placeholder="(555) 123-4567"
            @keyup.enter="confirmBooking"
          />
          <p v-if="bookError" class="ts-book-error">{{ bookError }}</p>
          <div class="ts-verify-btns">
            <button class="ts-btn-nav" @click="step = 'time'">
              <i class="fas fa-arrow-left"></i> Back
            </button>
            <button
              class="ts-btn-confirm"
              :disabled="!phone || submitting"
              @click="confirmBooking"
            >
              <i v-if="submitting" class="fas fa-spinner fa-spin"></i>
              <span v-else>Confirm</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axiosLib from 'axios';

// Use VUE_APP_API_BASE_URL so dev hits http://pixiev2:3000 and prod hits
// https://pixie.bigtapapp.com (same-origin -> no mixed content).
const api = axiosLib.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || '',
});

export default {
  name: 'TenantSchedule',
  data() {
    return {
      loading: true,
      pageError: '',
      step: 'day',
      workDays: [],
      address: '',
      fee: 0,
      tenantName: '',
      navStartDate: null,
      selectedDay: null,
      selectedBlock: null,
      phone: '',
      bookError: '',
      submitting: false,
      booked: false,
      bookedDetails: {},
      woId: '',
      key: '',
    };
  },
  computed: {
    dayOffset() {
      if (!this.workDays || this.workDays.length === 0) return 0;
      const map = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4 };
      return map[this.workDays[0].dayOfWeek] || 0;
    },
  },
  async mounted() {
    this.woId = this.$route.query.woId;
    this.key = this.$route.query.key;
    if (!this.woId || !this.key) {
      this.pageError = 'Invalid link. Missing required parameters.';
      this.loading = false;
      return;
    }
    await this.loadSlots();
  },
  methods: {
    async loadSlots() {
      this.loading = true;
      try {
        const params = { key: this.key, woId: this.woId };
        if (this.navStartDate) params.startDate = this.navStartDate;
        const res = await api.get('/api/workorder-schedule/tenant-slots', {
          params,
        });
        this.workDays = res.data.workDays;
        this.address = res.data.address;
        this.fee = res.data.fee;
        this.tenantName = res.data.tenantName || '';
      } catch (err) {
        if (err.response && err.response.status === 404) {
          this.pageError = 'This link has expired or is invalid.';
        } else {
          this.pageError = 'Unable to load available times. Please try again.';
        }
      }
      this.loading = false;
    },
    selectDay(day) {
      this.selectedDay = day;
      this.selectedBlock = null;
      this.step = 'time';
    },
    selectBlock(block) {
      this.selectedBlock = block;
      this.bookError = '';
      this.step = 'verify';
    },
    async loadNext() {
      if (this.workDays.length === 0) return;
      const last = this.workDays[this.workDays.length - 1];
      const d = new Date(last.date + 'T00:00:00');
      d.setDate(d.getDate() + 1);
      this.navStartDate = this.fmtDate(d);
      await this.loadSlots();
      this.step = 'day';
    },
    async loadPrevious() {
      if (!this.navStartDate) return;
      const d = new Date(this.navStartDate + 'T00:00:00');
      let count = 0;
      while (count < 15) {
        d.setDate(d.getDate() - 1);
        if (d.getDay() !== 0 && d.getDay() !== 6) count++;
      }
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      if (d <= tomorrow) {
        this.navStartDate = null;
      } else {
        this.navStartDate = this.fmtDate(d);
      }
      await this.loadSlots();
      this.step = 'day';
    },
    async confirmBooking() {
      if (!this.phone || this.submitting) return;
      this.submitting = true;
      this.bookError = '';
      try {
        const res = await api.post('/api/workorder-schedule/tenant-book', {
          woId: this.woId,
          key: this.key,
          phone: this.phone,
          date: this.selectedDay.date,
          startTime: this.selectedBlock.start,
          endTime: this.selectedBlock.end,
        });
        this.bookedDetails = {
          address: res.data.appointment.address,
          date: res.data.appointment.date,
          startTime: this.selectedBlock.label.split(' - ')[0],
          endTime: this.selectedBlock.label.split(' - ')[1],
          fee: res.data.appointment.fee,
        };
        this.booked = true;
      } catch (err) {
        const status = err.response && err.response.status;
        const data = err.response && err.response.data;
        const msg = (data && data.error) || 'Booking failed. Please try again.';
        if (status === 409 || status === 404 || (data && data.locked)) {
          this.pageError = msg;
        } else {
          this.bookError = msg;
        }
      }
      this.submitting = false;
    },
    fmtDate(d) {
      return (
        d.getFullYear() +
        '-' +
        String(d.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(d.getDate()).padStart(2, '0')
      );
    },
    formatDateLong(dateStr) {
      const d = new Date(dateStr + 'T12:00:00');
      return d.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
  },
};
</script>

<style scoped>
.ts-page {
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: #f5f0f7;
  padding: 20px 12px;
  box-sizing: border-box;
  font-family: Arial, sans-serif;
}
.ts-card {
  background: #fff;
  border-radius: 14px;
  padding: 24px 18px;
  max-width: 420px;
  width: 100%;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.1);
}
.ts-title {
  margin: 0 0 4px;
  font-size: 1.3em;
  color: #5a3e6a;
  text-align: center;
}
.ts-address {
  text-align: center;
  color: #555;
  margin: 0 0 8px;
  font-size: 0.95em;
}
.ts-fee {
  text-align: center;
  color: #c97800;
  margin: 0 0 16px;
  font-size: 0.95em;
}
.ts-instruction {
  font-weight: 600;
  color: #5a3e6a;
  margin: 0 0 10px;
  font-size: 1em;
}
.ts-sub-instruction {
  color: #555;
  margin: 0 0 14px;
  line-height: 1.5;
}
.ts-loading {
  text-align: center;
  padding: 40px 0;
  color: #888;
}
.ts-loading i {
  font-size: 2em;
  margin-bottom: 12px;
  display: block;
  color: #6e5875;
}
.ts-error-state {
  text-align: center;
  padding: 32px 0;
  color: #555;
}
.ts-icon {
  font-size: 48px;
  margin-bottom: 12px;
}
.ts-icon-error {
  color: #e53935;
}
.ts-icon-success {
  color: #4caf50;
}
.ts-confirmed {
  text-align: center;
  padding: 16px 0;
}
.ts-confirmed h2 {
  color: #4caf50;
  margin: 8px 0 16px;
}
.ts-confirm-details {
  line-height: 1.6;
  color: #333;
}
.ts-note {
  color: #888;
  font-size: 0.9em;
  margin-top: 16px;
}

/* Day grid */
.ts-day-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
  margin-bottom: 12px;
}
.ts-day-spacer {
  min-height: 1px;
}
.ts-day-card {
  background: #f8f5fa;
  border: 2px solid #d4c8db;
  border-radius: 10px;
  padding: 10px 2px;
  text-align: center;
  cursor: pointer;
  transition:
    border-color 0.15s,
    transform 0.1s;
}
.ts-day-card:active {
  transform: scale(0.95);
}
.ts-day-card:not(.ts-day-unavailable):hover {
  border-color: #6e5875;
}
.ts-day-unavailable {
  opacity: 0.4;
  cursor: default;
}
.ts-day-name {
  font-weight: 700;
  font-size: 0.8em;
  color: #6e5875;
}
.ts-day-date {
  font-size: 0.85em;
  color: #333;
  margin-top: 2px;
}

/* Nav row */
.ts-nav-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
}
.ts-btn-nav {
  padding: 10px 16px;
  background: none;
  border: 1px solid #c4b5ce;
  border-radius: 8px;
  color: #6e5875;
  cursor: pointer;
  font-size: 0.9em;
}
.ts-btn-nav:active {
  background: #f0e8f3;
}

/* Time list */
.ts-time-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 14px;
}
.ts-time-card {
  padding: 14px 16px;
  background: #f8f5fa;
  border: 2px solid #d4c8db;
  border-radius: 10px;
  cursor: pointer;
  font-size: 1em;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition:
    border-color 0.15s,
    transform 0.1s;
}
.ts-time-card:not(.ts-time-unavail):active {
  transform: scale(0.97);
}
.ts-time-card:not(.ts-time-unavail):hover {
  border-color: #6e5875;
}
.ts-time-unavail {
  opacity: 0.4;
  cursor: default;
}
.ts-badge {
  font-size: 0.8em;
  color: #c97878;
}

/* Verify step */
.ts-label {
  display: block;
  font-size: 0.9em;
  color: #555;
  margin-bottom: 6px;
}
.ts-input {
  width: 100%;
  padding: 12px 14px;
  border: 2px solid #d4c8db;
  border-radius: 8px;
  font-size: 1em;
  box-sizing: border-box;
  outline: none;
}
.ts-input:focus {
  border-color: #6e5875;
}
.ts-book-error {
  color: #e53935;
  font-size: 0.9em;
  margin: 8px 0 0;
}
.ts-verify-btns {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 16px;
}
.ts-btn-confirm {
  flex: 1;
  padding: 12px 20px;
  background: #6e5875;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
}
.ts-btn-confirm:active {
  background: #5a3e6a;
}
.ts-btn-confirm:disabled {
  opacity: 0.5;
  cursor: default;
}
</style>
